// Trilha do certificado: o que aconteceu com ele e QUEM fez.
//
// O portal audita a chamada no log dele, mas essa trilha vive no payment — e é
// aqui que se olha quando um certificado é trocado indevidamente.
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { useMerchantMtlsCertificateHistory } from "@/services/admin/merchant-mtls-certificates"

const panelPrefix = "pages.adminMerchants"

// Paleta semântica: o que falhou em vermelho, o que ficou pendente em âmbar.
const STATUS_STYLES: Record<string, string> = {
  APPLIED:
    "border-emerald-200 bg-emerald-50 text-emerald-700",
  PENDING: "border-amber-200 bg-amber-50 text-amber-700",
  PENDING_MANUAL:
    "border-amber-200 bg-amber-50 text-amber-700",
  FAILED: "border-red-200 bg-red-50 text-red-700",
}

type MtlsCertificateHistoryProps = {
  certificateId: string
}

export const MtlsCertificateHistory = ({
  certificateId,
}: MtlsCertificateHistoryProps) => {
  const { t } = useTranslation()
  const { data: events, isLoading } =
    useMerchantMtlsCertificateHistory(certificateId)

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-14 w-full" />
        ))}
      </div>
    )
  }

  const items = events ?? []

  if (items.length === 0) {
    return (
      <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        {t(`${panelPrefix}.mtlsCertificates.historyEmpty`)}
      </p>
    )
  }

  return (
    <ul className="space-y-2">
      {items.map((event) => (
        <li
          key={event.id}
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
        >
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">
                {t(
                  `${panelPrefix}.mtlsCertificates.eventType.${event.eventType}`,
                  { defaultValue: event.eventType }
                )}
              </span>
              <Badge
                variant="outline"
                className={
                  STATUS_STYLES[event.status] ??
                  "border-gray-200 bg-gray-50 text-gray-700"
                }
              >
                {event.status}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {/* Sem autor = scheduler. É o que distingue renovação automática
                  de alguém tendo renovado. */}
              {event.userId
                ? t(
                    `${panelPrefix}.mtlsCertificates.eventBy`,
                    { user: event.userId }
                  )
                : t(
                    `${panelPrefix}.mtlsCertificates.eventAutomatic`
                  )}
              {event.newFingerprintSha256
                ? ` · ${event.newFingerprintSha256}`
                : ""}
            </p>
            {event.errorMessage && (
              <p className="text-xs text-red-700">
                {event.errorMessage}
              </p>
            )}
          </div>
          <span className="text-xs tabular-nums text-muted-foreground">
            {event.createdAt}
          </span>
        </li>
      ))}
    </ul>
  )
}
